import type { SvgIconComponent } from '@mui/icons-material';
import HomeOutlined from '@mui/icons-material/HomeOutlined';
import BusinessOutlined from '@mui/icons-material/BusinessOutlined';
import LayersOutlined from '@mui/icons-material/LayersOutlined';
import CellTowerOutlined from '@mui/icons-material/CellTowerOutlined';
import SmartphoneOutlined from '@mui/icons-material/SmartphoneOutlined';
import ListAltOutlined from '@mui/icons-material/ListAltOutlined';
import ExtensionOutlined from '@mui/icons-material/ExtensionOutlined';
import StorageOutlined from '@mui/icons-material/StorageOutlined';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import ReportOutlined from '@mui/icons-material/ReportOutlined';
import TimelineOutlined from '@mui/icons-material/TimelineOutlined';
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';
import PhoneDisabledOutlined from '@mui/icons-material/PhoneDisabledOutlined';
import DevicesOtherOutlined from '@mui/icons-material/DevicesOtherOutlined';
import NotificationsOutlined from '@mui/icons-material/NotificationsOutlined';
import SendOutlined from '@mui/icons-material/SendOutlined';
import FingerprintOutlined from '@mui/icons-material/FingerprintOutlined';
import LinkOutlined from '@mui/icons-material/LinkOutlined';
import CallMergeOutlined from '@mui/icons-material/CallMergeOutlined';
import VerifiedUserOutlined from '@mui/icons-material/VerifiedUserOutlined';
import MonitorHeartOutlined from '@mui/icons-material/MonitorHeartOutlined';
import FactCheckOutlined from '@mui/icons-material/FactCheckOutlined';
import VisibilityOutlined from '@mui/icons-material/VisibilityOutlined';
import WorkOutlineOutlined from '@mui/icons-material/WorkOutlineOutlined';

/**
 * The Site Admin console navigation model — the single source of truth for the
 * sidebar (`SaSidebar`), the routed pages (`routes.tsx` flattens `SA_ROUTES`),
 * and the breadcrumb (`resolveSaTrail`). Mirrors the prototype `SA_NAV`
 * (`siteadmin/sa-data.js`): two ungrouped top items, then grouped sections, and
 * a single external "Jobs" affordance. Label / subtitle copy lives in the
 * shared `siteAdmin` i18n namespace; icons are the MUI ports of the prototype's.
 */
export interface SaLeaf {
  id: string;
  /** `siteAdmin:nav.*` label key (sidebar row + breadcrumb tail). */
  labelKey: string;
  icon: SvgIconComponent;
  /** Absolute route path under `/site`. */
  to: string;
  /** Optional page-title override key (`siteAdmin:pageTitle.*`); defaults to `labelKey`. */
  titleKey?: string;
  /** Page subtitle key (`siteAdmin:subtitle.*`). */
  subtitleKey: string;
}

export interface SaGroupItem {
  id: string;
  labelKey: string;
  icon: SvgIconComponent;
  children: SaLeaf[];
}

export interface SaExternalItem {
  id: string;
  labelKey: string;
  icon: SvgIconComponent;
  /**
   * Path on the **API** host, appended to `VITE_API_BASE_URL` and opened in a new
   * tab with a bearer token attached. Deliberately not a plain href: the Hangfire
   * dashboard is served by the API, not the SPA, so a relative URL would resolve
   * against the SPA origin and land on nothing (BUG-012).
   */
  apiPath: string;
}

export type SaNavEntry = SaLeaf | SaGroupItem | SaExternalItem;

export interface SaNavSection {
  /** `siteAdmin:nav.groups.*` section heading; omitted for the top items. */
  groupKey?: string;
  items: SaNavEntry[];
}

export function isSaGroup(entry: SaNavEntry): entry is SaGroupItem {
  return 'children' in entry;
}
export function isSaExternal(entry: SaNavEntry): entry is SaExternalItem {
  return 'apiPath' in entry;
}

const leaf = (
  id: string,
  labelKey: string,
  icon: SvgIconComponent,
  to: string,
  subtitleKey: string,
  titleKey?: string,
): SaLeaf => ({ id, labelKey, icon, to, subtitleKey, titleKey });

export const SA_NAV: SaNavSection[] = [
  {
    items: [
      leaf(
        'home',
        'siteAdmin:nav.home',
        HomeOutlined,
        '/site',
        'siteAdmin:subtitle.home',
        'siteAdmin:pageTitle.home',
      ),
      leaf(
        'customers',
        'siteAdmin:nav.customers',
        BusinessOutlined,
        '/site/customers',
        'siteAdmin:subtitle.customers',
      ),
    ],
  },
  {
    groupKey: 'siteAdmin:nav.groups.platformData',
    items: [
      {
        id: 'catalog',
        labelKey: 'siteAdmin:nav.catalog',
        icon: LayersOutlined,
        children: [
          leaf(
            'catalog-carriers',
            'siteAdmin:nav.catalogCarriers',
            CellTowerOutlined,
            '/site/catalog/carriers',
            'siteAdmin:subtitle.catalogCarriers',
          ),
          leaf(
            'catalog-devices',
            'siteAdmin:nav.catalogDevices',
            SmartphoneOutlined,
            '/site/catalog/devices',
            'siteAdmin:subtitle.catalogDevices',
          ),
          leaf(
            'catalog-plans',
            'siteAdmin:nav.catalogPlans',
            ListAltOutlined,
            '/site/catalog/plans',
            'siteAdmin:subtitle.catalogPlans',
          ),
          leaf(
            'catalog-features',
            'siteAdmin:nav.catalogFeatures',
            ExtensionOutlined,
            '/site/catalog/features',
            'siteAdmin:subtitle.catalogFeatures',
          ),
        ],
      },
      {
        id: 'ingestion',
        labelKey: 'siteAdmin:nav.ingestion',
        icon: StorageOutlined,
        children: [
          leaf(
            'ing-recent',
            'siteAdmin:nav.ingRecent',
            DescriptionOutlined,
            '/site/ingestion/recent',
            'siteAdmin:subtitle.ingRecent',
          ),
          leaf(
            'ing-errors',
            'siteAdmin:nav.ingErrors',
            ReportOutlined,
            '/site/ingestion/errors',
            'siteAdmin:subtitle.ingErrors',
          ),
          leaf(
            'ing-feeds',
            'siteAdmin:nav.ingFeeds',
            TimelineOutlined,
            '/site/ingestion/feeds',
            'siteAdmin:subtitle.ingFeeds',
          ),
          leaf(
            'ing-warnings',
            'siteAdmin:nav.ingWarnings',
            WarningAmberOutlined,
            '/site/ingestion/warnings',
            'siteAdmin:subtitle.ingWarnings',
          ),
          leaf(
            'ing-unmatched-lines',
            'siteAdmin:nav.ingUnmatchedLines',
            PhoneDisabledOutlined,
            '/site/ingestion/unmatched-lines',
            'siteAdmin:subtitle.ingUnmatchedLines',
          ),
          leaf(
            'ing-unmatched-devices',
            'siteAdmin:nav.ingUnmatchedDevices',
            DevicesOtherOutlined,
            '/site/ingestion/unmatched-devices',
            'siteAdmin:subtitle.ingUnmatchedDevices',
          ),
        ],
      },
      {
        id: 'notifications',
        labelKey: 'siteAdmin:nav.notifications',
        icon: NotificationsOutlined,
        children: [
          leaf(
            'notif-templates',
            'siteAdmin:nav.notifTemplates',
            DescriptionOutlined,
            '/site/notifications/templates',
            'siteAdmin:subtitle.notifTemplates',
          ),
          leaf(
            'notif-delivery',
            'siteAdmin:nav.notifDelivery',
            SendOutlined,
            '/site/notifications/delivery',
            'siteAdmin:subtitle.notifDelivery',
          ),
        ],
      },
    ],
  },
  {
    groupKey: 'siteAdmin:nav.groups.governance',
    items: [
      {
        id: 'identity',
        labelKey: 'siteAdmin:nav.identity',
        icon: FingerprintOutlined,
        children: [
          leaf(
            'id-entra',
            'siteAdmin:nav.idEntra',
            LinkOutlined,
            '/site/identity/entra',
            'siteAdmin:subtitle.idEntra',
          ),
          leaf(
            'id-merge',
            'siteAdmin:nav.idMerge',
            CallMergeOutlined,
            '/site/identity/merge',
            'siteAdmin:subtitle.idMerge',
            'siteAdmin:pageTitle.idMerge',
          ),
          leaf(
            'id-access',
            'siteAdmin:nav.idAccess',
            VerifiedUserOutlined,
            '/site/identity/access',
            'siteAdmin:subtitle.idAccess',
            'siteAdmin:pageTitle.idAccess',
          ),
        ],
      },
      leaf(
        'usage',
        'siteAdmin:nav.usage',
        MonitorHeartOutlined,
        '/site/usage',
        'siteAdmin:subtitle.usage',
      ),
      {
        id: 'audit',
        labelKey: 'siteAdmin:nav.audit',
        icon: FactCheckOutlined,
        children: [
          leaf(
            'audit-log',
            'siteAdmin:nav.auditLog',
            ListAltOutlined,
            '/site/audit/log',
            'siteAdmin:subtitle.auditLog',
          ),
          leaf(
            'audit-imp',
            'siteAdmin:nav.auditImpersonation',
            VisibilityOutlined,
            '/site/audit/impersonation',
            'siteAdmin:subtitle.auditImpersonation',
          ),
        ],
      },
    ],
  },
  {
    groupKey: 'siteAdmin:nav.groups.system',
    items: [
      {
        id: 'jobs',
        labelKey: 'siteAdmin:nav.jobs',
        icon: WorkOutlineOutlined,
        // The token-for-cookie bridge, not the dashboard itself: it authenticates
        // the tab and redirects to /hangfire.
        apiPath: '/hangfire-auth',
      },
    ],
  },
];

/** Flat list of every routed leaf (top-level + children), for `routes.tsx`. */
export const SA_ROUTES: SaLeaf[] = SA_NAV.flatMap((section) =>
  section.items.flatMap((item) => {
    if (isSaExternal(item)) return [];
    if (isSaGroup(item)) return item.children;
    return [item];
  }),
);

/** Resolves the active leaf (+ its parent group, if any) for a pathname. */
export function resolveSaLeaf(pathname: string): { leaf: SaLeaf; parent?: SaGroupItem } | null {
  for (const section of SA_NAV) {
    for (const item of section.items) {
      if (isSaExternal(item)) continue;
      if (isSaGroup(item)) {
        const child = item.children.find((c) => matches(c.to, pathname));
        if (child) return { leaf: child, parent: item };
      } else if (matches(item.to, pathname)) {
        return { leaf: item };
      }
    }
  }
  return null;
}

/** Breadcrumb label keys after the DecisionPoint wordmark (`parent?`, `leaf`). */
export function resolveSaTrail(pathname: string): string[] {
  const hit = resolveSaLeaf(pathname);
  if (!hit) return [];
  const tail = hit.leaf.titleKey ?? hit.leaf.labelKey;
  return hit.parent ? [hit.parent.labelKey, tail] : [tail];
}

// `/site` (Home) matches only exactly; every other leaf matches itself and its
// descendants (e.g. a future `/site/customers/:id` keeps "Customers" active).
function matches(to: string, pathname: string): boolean {
  if (to === '/site') return pathname === '/site' || pathname === '/site/';
  return pathname === to || pathname.startsWith(`${to}/`);
}
